import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useOrganization } from "@/contexts/OrganizationContext";
import { DateRangePicker } from "./DateRangePicker";
import {
  CustomizeViewDrawer,
  type UsageViewPreferences,
} from "./CustomizeViewDrawer";
import { useProjectsLookup, useUsersLookup } from "../api";
import type { MetricScope } from "../api";

type FiltersBarProps = {
  startDate: Date;
  endDate: Date;
  onDateChange: (start: Date, end: Date) => void;
  userId: string;
  onUserChange: (id: string) => void;
  projectIds: string[];
  onProjectChange: (ids: string[]) => void;
  metricScope: MetricScope;
  onMetricScopeChange: (scope: MetricScope) => void;
  viewPreferences: UsageViewPreferences;
  onViewPreferencesChange: (preferences: UsageViewPreferences) => void;
};

export function FiltersBar({
  startDate,
  endDate,
  onDateChange,
  userId,
  onUserChange,
  projectIds,
  onProjectChange,
  metricScope,
  onMetricScopeChange,
  viewPreferences,
  onViewPreferencesChange,
}: FiltersBarProps) {
  const { t } = useTranslation();
  const { currentOrganization } = useOrganization();
  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const [projectPickerOpen, setProjectPickerOpen] = useState(false);
  const hasOrg = Boolean(currentOrganization?.id);
  const isOwner = hasOrg && currentOrganization?.role_id === "owner";
  const users = useUsersLookup(currentOrganization?.id, isOwner);
  const projects = useProjectsLookup(currentOrganization?.id, hasOrg);
  const selectedUser = users.data?.find((user) => user.value === userId);
  const selectedProjectNames = projectIds
    .map((id) => projects.data?.find((project) => project.value === id)?.label)
    .filter((name): name is string => Boolean(name));
  const projectPickerLabel =
    projectIds.length === 0
      ? t("usageAndCost.filters.allProjects")
      : projectIds.length === 1
        ? selectedProjectNames[0] || projectIds[0]
        : t("usageAndCost.filters.projectsSelected", {
            count: projectIds.length,
          });

  return (
    <div className="flex flex-col gap-3 md:grid md:grid-cols-[minmax(0,1fr)_auto] md:items-start">
      <div className="flex w-full min-w-0 flex-col gap-3 md:flex-row md:flex-wrap md:items-center">
        <div className="w-full min-w-0 md:w-auto md:flex-[2_1_340px]">
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onChange={onDateChange}
          />
        </div>

        {isOwner && (
          <Popover open={userPickerOpen} onOpenChange={setUserPickerOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                role="combobox"
                aria-expanded={userPickerOpen}
                className="flex h-10 w-full min-w-0 items-center justify-between gap-2 rounded-md border border-input bg-input px-3 text-sm md:w-auto md:max-w-[240px] md:flex-[1_1_200px]"
              >
                <span className="truncate">
                  {userId === "all"
                    ? t("usageAndCost.filters.allUsers")
                    : selectedUser?.label || userId}
                </span>
                <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="w-[var(--radix-popover-trigger-width)] p-0"
              onOpenAutoFocus={(event) => event.preventDefault()}
            >
              <Command>
                <CommandInput
                  placeholder={t("usageAndCost.filters.searchUsers")}
                  className="border-0 shadow-none outline-none ring-0 focus:border-0 focus:outline-none focus:ring-0 focus-visible:border-0 focus-visible:outline-none focus-visible:ring-0"
                />
                <CommandList>
                  <CommandEmpty>
                    {t("usageAndCost.filters.noUsersFound")}
                  </CommandEmpty>
                  <CommandItem
                    value={t("usageAndCost.filters.allUsers")}
                    onSelect={() => {
                      onUserChange("all");
                      setUserPickerOpen(false);
                    }}
                  >
                    <Check
                      className={`size-4 ${userId === "all" ? "opacity-100" : "opacity-0"}`}
                    />
                    {t("usageAndCost.filters.allUsers")}
                  </CommandItem>
                  {(users.data ?? []).map((user) => (
                    <CommandItem
                      key={user.value}
                      value={`${user.label} ${user.value}`}
                      onSelect={() => {
                        onUserChange(user.value);
                        setUserPickerOpen(false);
                      }}
                    >
                      <Check
                        className={`size-4 ${userId === user.value ? "opacity-100" : "opacity-0"}`}
                      />
                      <span className="truncate">
                        {user.label || user.value}
                      </span>
                    </CommandItem>
                  ))}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}

        {/* <Select value={metricScope} onValueChange={onMetricScopeChange}>
          <SelectTrigger
            aria-label={t("usageAndCost.filters.metricScope")}
            className="h-10 w-full min-w-0 bg-input md:w-auto md:min-w-[180px]"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent align="start">
            <SelectItem value="usage">
              {t("usageAndCost.filters.usage")}
            </SelectItem>
            <SelectItem value="usage_and_cost">
              {t("usageAndCost.filters.costAndUsage")}
            </SelectItem>
          </SelectContent>
        </Select> */}

        {hasOrg && (
          <Popover open={projectPickerOpen} onOpenChange={setProjectPickerOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                role="combobox"
                aria-expanded={projectPickerOpen}
                className="flex h-10 w-full min-w-0 items-center justify-between gap-2 rounded-md border border-input bg-input px-3 text-sm md:w-auto md:max-w-[240px] md:flex-[1_1_200px]"
              >
                <span className="truncate">{projectPickerLabel}</span>
                <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="w-[var(--radix-popover-trigger-width)] p-0"
              onOpenAutoFocus={(event) => event.preventDefault()}
            >
              <Command>
                <CommandInput
                  placeholder={t("usageAndCost.filters.searchProjects")}
                  className="border-0 shadow-none outline-none ring-0 focus:border-0 focus:outline-none focus:ring-0 focus-visible:border-0 focus-visible:outline-none focus-visible:ring-0"
                />
                <CommandList>
                  <CommandEmpty>
                    {t("usageAndCost.filters.noProjectsFound")}
                  </CommandEmpty>
                  <CommandItem
                    value={t("usageAndCost.filters.allProjects")}
                    onSelect={() => onProjectChange([])}
                  >
                    <Check
                      className={`size-4 ${projectIds.length === 0 ? "opacity-100" : "opacity-0"}`}
                    />
                    {t("usageAndCost.filters.allProjects")}
                  </CommandItem>
                  {(projects.data ?? []).map((project) => {
                    const selected = projectIds.includes(project.value);
                    return (
                      <CommandItem
                        key={project.value}
                        value={`${project.label} ${project.value}`}
                        onSelect={() =>
                          onProjectChange(
                            selected
                              ? projectIds.filter((id) => id !== project.value)
                              : [...projectIds, project.value],
                          )
                        }
                      >
                        <Check
                          className={`size-4 ${selected ? "opacity-100" : "opacity-0"}`}
                        />
                        <span className="truncate">{project.label || "—"}</span>
                      </CommandItem>
                    );
                  })}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}
      </div>

      <CustomizeViewDrawer
        preferences={viewPreferences}
        onSave={onViewPreferencesChange}
      />
    </div>
  );
}
